import { Component, ViewChild } from '@angular/core';
import { CalendarView } from 'angular-calendar';
import { BaseComponent } from 'src/app/core/base.component';
import { SessionService } from 'src/app/shared/services/session.service';
import { BookingEventEmitters } from '../booking-extensions/services/booking-event-emitters';
import { StringHelpers } from 'src/app/shared/helpers/string-helpers';
import { OperatingHours } from '../models/operating-hours';
import { AccountService } from 'src/app/account/account.service';
import { AlertModalComponent } from 'src/app/shared/modals/alert-modal/alert-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { BookingEditModalComponent } from '../modals/booking/booking-edit-modal/booking-edit-modal.component';
import { Simulator } from '../models/simulators';
import { BookingService } from '../services/booking.service';
import { BookingSchedule } from '../models/booking-schedule';
import { BookingRefund } from '../models/booking-refund';
import { BookingStatusEnum } from 'src/app/shared/models/booking-status.enum';
import { EventSettingsModel, GroupModel, ScheduleComponent, TimeScaleModel, View } from '@syncfusion/ej2-angular-schedule';
import { WorkHoursModel } from '@syncfusion/ej2-angular-schedule';
import * as moment from 'moment-timezone';
import { diagonalDownProperty } from '@syncfusion/ej2/documenteditor';

@Component({
    selector: 'base-scheduler-v2',
    template: '',
    standalone: false
})
export class BaseBookingSchedulerV2Component extends BaseComponent {
    public CalendarView = CalendarView;
    public currentView: View = 'Day';
    public selectedDate: Date = new Date();
    public minDate: Date = new Date().addDays(-30);
    public maxDate: Date = new Date().addDays(360);
    public dayStartHour: string;
    public dayEndHour: string;
    protected userDetails: any;
    protected paymentIntent: any;
    protected operatingHours: OperatingHours[];
    protected workHours: WorkHoursModel = { highlight: true, start: '00:00', end: '24:00' };
    protected timeScaleModel: TimeScaleModel = {}
    protected isInitializing: boolean = true;

    views: View[] = ['Day', 'Month'];
    eventSettings: EventSettingsModel = {
        dataSource: [],
        enableTooltip: false,
        fields: {
            id: 'Id',
            subject: { name: 'Subject' },
            startTime: { name: 'StartTime' },
            startTimezone: { name: 'StartTimezone' },
            endTime: { name: 'EndTime' },
            endTimezone: { name: 'EndTimezone' },
            description: { name: 'Description' },
            location: { name: 'Location' }
        }
    };
    group: GroupModel = { resources: ['simulators'], enableCompactView: false };

    @ViewChild('scheduleObj')
    public scheduleObj: ScheduleComponent;

    constructor(sessionService: SessionService,
        protected bookingEventEmitters: BookingEventEmitters,
        protected stringHelpers: StringHelpers,
        protected accountService: AccountService,
        protected dialog: MatDialog,
        protected bookingService: BookingService) {
        super(sessionService, stringHelpers);
        this.getUserDetails();
    }

    getUserDetails() {
        this.isInitializing = true;
        this.accountService.getUserByAuth0Id(this.sessionService.getUser().auth0Id).subscribe({
            next: (result) => {
                this.userDetails = result;
                this.isInitializing = false;
                this.bookingEventEmitters.baseFinishedInitializingEvent.emit();
            }, error: (error) => {
                console.error(error);
                this.isInitializing = false;
            }, complete: () => {
                this.setOperatingHours(new Date());
            }
        });
    }

    protected setOperatingHours(date: Date): void {
        this.operatingHours = this.sessionService.getUser().operatingHours ?
            JSON.parse(this.sessionService.getUser().operatingHours as unknown as string) as OperatingHours[] :
            [];

        if (this.operatingHours.length > 0) {
            const today = date.getDay();
            const todayHours = this.operatingHours.find(h => h.dayOfWeek === today);
            if (todayHours && todayHours.isOpen) {
                this.dayStartHour = todayHours.openTime.toString();
                this.dayEndHour = todayHours.isNextDay || todayHours.closeTime.toString() === "00:00" ? "24:00" : todayHours.closeTime.toString();

                this.workHours = {
                    start: this.dayStartHour,
                    end: this.dayEndHour,
                    highlight: true,
                };
            }
        }
    }

    // private isDateValid(date: Date): boolean {
    //     return /*isToday(date) ||*/ date >= this.minDate && date <= this.maxDate;
    // }

    alert(title, body, submitText) {
        const dialogRef = this.dialog.open(AlertModalComponent, {
            width: '50vw',
        });

        dialogRef.componentInstance.title = title;
        dialogRef.componentInstance.body = body;
        dialogRef.componentInstance.submitText = submitText;

        dialogRef.afterClosed().subscribe(() => {
            // this.refresh.next();
        });
    }

    cancelBooking(event: any, booking: BookingSchedule = null) {
        const dialogRef = this.dialog.open(AlertModalComponent, {
            width: '50vw'
        });

        dialogRef.componentInstance.title = "Cancel or Delete Booking";
        dialogRef.componentInstance.body = "Please confirm if you would like to cancel or permanently delete this booking. Once you permanently delete a booking, it will be removed from the Scheduler and All Bookings. Once this action is completed, you will not be able to recover it.";

        if (booking.hasPaid) {
            dialogRef.componentInstance.body += "<br /><br />There is a payment associted with this booking. It will be automatically refunded to the customer.";
        }
        dialogRef.componentInstance.submitText = "Cancel Booking";
        dialogRef.componentInstance.isOtherTextDisabled = false;
        dialogRef.componentInstance.otherText = "Delete Booking"

        if (event.Status == 'Past') {
            dialogRef.componentInstance.title = "Delete Booking";
            dialogRef.componentInstance.body = "Once you permanently delete a booking, it will be removed from the Scheduler and All Bookings section. This action cannot be undone.";
            dialogRef.componentInstance.submitText = "Okay";
            dialogRef.componentInstance.isOtherTextDisabled = true;
        }

        dialogRef.beforeClosed().subscribe((result: string) => {
            if (result == "no") {
                return;
            }

            if (booking.hasPaid &&
                !this.isBookingInProgress({ start: booking.startTime, end: booking.endTime }) &&
                !this.isBookingPast(new Date(booking.endTime))) {

                this.isLoading = true;
                let data = {
                    accountId: this.sessionService.getUser().stripeAccountId,
                    options: {
                        query: `metadata['BookingId']:'${booking.id}'`
                    }
                };

                booking.status = BookingStatusEnum.Cancelled;
                booking.lastEditedBy = this.sessionService.getUserId();
                booking.organizationId = this.sessionService.getUser().organizationId;

                this.bookingService.getPaymentMethodForRefundOrAdditionalPayment(data)
                    .subscribe({
                        next: (paymentIntent: any) => {
                            this.paymentIntent = paymentIntent.find(x => x.metadata.Parent === 'true');
                            let bookingRefund = BookingRefund.mapFromBookingSchedule(booking, booking.totalPrice, "Cancelled from Scheduler", booking.participants);
                            bookingRefund.totalPrice = 0;
                            bookingRefund.addons = booking.addons;
                            bookingRefund['timeZone'] = this.sessionService.getUser().timeZone;

                            let refundData = {
                                stripeAccountId: this.sessionService.getUser().stripeAccountId,
                                stripeChargeId: this.paymentIntent.latest_charge,
                                booking: bookingRefund,
                                isPartialRefund: false
                            };

                            if(this.paymentIntent == null || this.paymentIntent == undefined) {
                                dialogRef.componentInstance.title = "Payment";
                                dialogRef.componentInstance.body = "Payment is still processing with Stripe. Please try again in a few minutes.";
                                dialogRef.componentInstance.submitText = "Okay";
                                dialogRef.componentInstance.isOtherTextDisabled = true;
                                return;
                            }

                            this.bookingService.refund(refundData)
                                .subscribe({
                                    next: (response: any) => {
                                        if (response.status === 200) {
                                            let data = {
                                                id: event.Id,
                                                isHardDelete: false,
                                                lastEditedBy: this.sessionService.getUserId()
                                            };

                                            if (result.toLowerCase() === "delete booking") {
                                                data.isHardDelete = true;
                                            }

                                            this.isLoading = true;
                                            this.bookingService.delete(data).subscribe({
                                                next: () => {
                                                    this.isLoading = false;
                                                    this.bookingEventEmitters.cancelBookingEvent.emit(booking);
                                                },
                                                error: (error) => {
                                                    this.isLoading = false;
                                                    console.error(error);
                                                },
                                            })
                                        }
                                    },
                                    error: (error: any) => {
                                        console.error(error);
                                        this.isLoading = false;
                                    },
                                    complete: () => this.isLoading = false
                                });
                        },
                        error: (error: any) => {
                            console.error(error);
                            this.isLoading = false;
                        }
                    });
            } else {
                this.isLoading = true;
                booking.status = BookingStatusEnum.Cancelled;
                booking.lastEditedBy = this.sessionService.getUserId();
                booking.organizationId = this.sessionService.getUser().organizationId;
                booking['timeZone'] = this.sessionService.getUser().timeZone;

                this.bookingService.update(booking.id, booking).subscribe({
                    next: () => {
                        this.bookingEventEmitters.cancelBookingEvent.emit(booking);
                    },
                    error: (error: any) => {
                        console.error(error);
                        this.isLoading = false;
                    },
                    complete: () => this.isLoading = false
                });
            }
        });
    }

    editBooking(booking: BookingSchedule, simulators: Simulator[]) {
        const dialogRef = this.dialog.open(BookingEditModalComponent, {
            width: '75vw',
            height: 'auto',
            disableClose: true
        });

        let startTime = this.stringHelpers.normalizeDateTime(moment.utc(booking.startTime).tz(this.userDetails.timeZone).format("YYYY-MM-DDTHH:mm:ss"));
        let endTime = this.stringHelpers.normalizeDateTime(moment.utc(booking.endTime).tz(this.userDetails.timeZone).format("YYYY-MM-DDTHH:mm:ss"));

        dialogRef.componentInstance.bookingId = booking.id;
        dialogRef.componentInstance.startTime = booking.startTime;
        dialogRef.componentInstance.simulators = simulators;
        dialogRef.componentInstance.form.get('simulatorId').setValue(booking.simulatorId);
        dialogRef.componentInstance.form.get('duration').setValue((new Date(booking.endTime).getTime() - new Date(booking.startTime).getTime()) / (1000 * 60));
        dialogRef.componentInstance.form.get('email').setValue(booking.email);
        dialogRef.componentInstance.form.get('customerName').setValue(booking.customerName);
        dialogRef.componentInstance.form.get('phoneNumber').setValue(booking.phoneNumber);
        dialogRef.componentInstance.form.get('participants').setValue(booking.participants);
        dialogRef.componentInstance.form.get('status').setValue(booking.status);
        dialogRef.componentInstance.form.get('startTime').setValue(startTime);
        dialogRef.componentInstance.form.get('endTime').setValue(endTime);
        dialogRef.componentInstance.form.get('addons').setValue(booking.addons);
        dialogRef.componentInstance.form.get('totalPrice').setValue(booking.totalPrice);
        dialogRef.componentInstance.form.get('hasPaid').setValue(booking.hasPaid);
        dialogRef.componentInstance.confirmationNumber = booking.confirmationNumber;
        dialogRef.componentInstance.userAccount = this.userDetails;
        dialogRef.componentInstance.bookingPaymentIntent = booking.stripePaymentIntentId;
        
        dialogRef.afterClosed().subscribe(result => {
            if (result === "save") {
                this.bookingEventEmitters.updateBookingEvent.emit();
            }
        });
    }

    isBookingInProgress(event: { start: Date | string, end: Date | string }): boolean {
        const now = new Date();
        const startTime = new Date(event.start);
        const endTime = new Date(event.end);

        // Compare just the dates to handle timezone differences
        const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const bookingStartDate = new Date(startTime.getFullYear(), startTime.getMonth(), startTime.getDate());

        // If the booking is for a future date, it's not in progress
        if (bookingStartDate > currentDate) {
            return false;
        }

        // If it's today, check the time
        if (bookingStartDate.getTime() === currentDate.getTime()) {
            return now >= startTime && now <= endTime;
        }

        return false;
    }

    isBookingPast(date: Date): boolean {
        if (date === undefined) {
            return false;
        }

        return date.getTime() < Date.now();
    }

    onCellClick($event) {
        console.log($event);
        $event.cancel = true;
    }

    onEventRendered(args) {
        if (args.data != undefined) {
            if (args.data.Status === 'Upcoming') {
                (args.element as any).style.background = '#4d77b6';
            } else if (args.data.Status === 'In Progress') {
                (args.element as any).style.background = '#76b676';
            } else if (args.data.Status === 'Past') {
                (args.element as any).style.background = '#ababab';
            } else if (args.data.Status === 'Member/Upcoming') {
                (args.element as any).style.background = '#1e88e5';
            } else if (args.data.Status === 'Member/In Progress') {
                (args.element as any).style.background = '#76b676';
            } else if (args.data.Status === 'Scheduled/Upcoming') {
                (args.element as any).style.background = '#cc8128';
            }else if(args.data.Status === 'Scheduled/In Progress') {
                (args.element as any).style.background = '#76b676';
            }
        }
    }

    onRenderCell(args: any) {
        if (this.isBookingPast(args.date)) {
            args.element.classList.add('past-slot');
        }
    }

    disableResize(args) {
        args.cancel = true;
    }

    onSelectedDateChange(args) {
        console.log(args);
    }

    isPreviousDayDisabled() {
        var currentDate = new Date();

        if (this.selectedDate === undefined) {
            return false;
        }

        var date = new Date(this.selectedDate.getFullYear(), this.selectedDate.getMonth(), this.selectedDate.getDate());
        var date2 = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
        var isDisabled = date.getTime() === date2.getTime();
        return isDisabled;
    }

    onDatePickerChange(event: any) {
        if (event.value) {
            this.selectedDate = event.value;
            event.isInteracted = false;
            this.setOperatingHours(this.selectedDate);
            this.bookingEventEmitters.dateChangeDateEvent.emit();
        }
    }
}
